import { AgentModelSelection } from '../components/agentChat/AgentChat.types';
import {
  emptyDryRunResult,
  type AutomationDryRunResult,
} from '../automation/dryRun';
import type { AutomationRunProgress } from '../automation/runProgress';
import type {
  AutomationGraph,
  AutomationTrigger,
  AutomationWorkflow,
} from '../types/AutomationWorkflow.types';
import { normalizeAutomationWorkflow } from '../types/AutomationWorkflow.types';
import { on, oncePromise, removeListener, run } from '../helper/electron';
import isElectron from '../utils/isElectron';

/**
 * Renderer side of saved automations. They live in the main process database,
 * so this is a thin pass-through over IPC.
 */

const CHANNELS = {
  list: 'automations/list',
  save: 'automations/save',
  delete: 'automations/delete',
  run: 'automations/run',
  runProgress: 'automations/run-progress',
  dryRun: 'automations/dry-run',
} as const;

export const areAutomationsAvailable = (): boolean => isElectron();

type AutomationWireRow = Partial<AutomationWorkflow> & {
  id: number;
  name: string;
  instructions?: string;
  executionLog?: AutomationWorkflow['executionLog'];
  createdAt: string;
};

const normalizeAutomationRow = (row: AutomationWireRow): AutomationWorkflow =>
  normalizeAutomationWorkflow({
    id: row.id,
    name: row.name,
    trigger: row.trigger,
    graph: row.graph,
    enabled: row.enabled,
    instructions: row.instructions ?? '',
    model: row.model,
    modelParams: row.modelParams,
    runCount: row.runCount ?? 0,
    lastRunAt: row.lastRunAt ?? null,
    lastRunId: row.lastRunId ?? null,
    lastStatus: row.lastStatus ?? null,
    executionLog: row.executionLog ?? [],
    createdAt: row.createdAt,
  });

const normalizeAutomationList = (
  automations: AutomationWireRow[] | undefined,
): AutomationWorkflow[] => (automations ?? []).map(normalizeAutomationRow);

export const fetchAutomations = async (): Promise<AutomationWorkflow[]> => {
  if (!areAutomationsAvailable()) return [];
  const response = oncePromise<{ automations: AutomationWireRow[] }>(
    CHANNELS.list,
  );
  run(CHANNELS.list);
  const { automations } = await response;
  return normalizeAutomationList(automations);
};

export const subscribeToAutomations = (
  listener: (automations: AutomationWorkflow[]) => void,
): (() => void) => {
  const handler = (payload: { automations?: AutomationWireRow[] }) => {
    if (payload?.automations) {
      listener(normalizeAutomationList(payload.automations));
    }
  };
  on(CHANNELS.list, handler);
  return () => removeListener(CHANNELS.list, handler);
};

export const saveAutomationWorkflow = async (input: {
  id?: number | null;
  name: string;
  trigger: AutomationTrigger;
  graph: AutomationGraph;
  enabled: boolean;
  model?: AgentModelSelection | null;
}): Promise<{
  saved: boolean;
  automation?: AutomationWorkflow;
  error?: string;
}> => {
  if (!areAutomationsAvailable()) {
    return { saved: false, error: 'Automations need the desktop app.' };
  }
  const response = oncePromise<{
    saved: boolean;
    automation?: AutomationWireRow;
    error?: string;
  }>(CHANNELS.save);
  run(CHANNELS.save, input);
  const result = await response;
  if (!result.saved || !result.automation) {
    return { saved: result.saved, error: result.error };
  }
  return {
    saved: true,
    automation: normalizeAutomationRow(result.automation),
  };
};

export const deleteAutomation = async (
  id: number,
): Promise<AutomationWorkflow[]> => {
  const response = oncePromise<{ automations: AutomationWireRow[] }>(
    CHANNELS.list,
  );
  run(CHANNELS.delete, { id });
  const { automations } = await response;
  return normalizeAutomationList(automations);
};

export const runAutomation = async (
  id: number,
): Promise<{ started: boolean; runId?: string; error?: string }> => {
  if (!areAutomationsAvailable()) {
    return { started: false, error: 'Automations need the desktop app.' };
  }
  const response = oncePromise<{
    started: boolean;
    runId?: string;
    error?: string;
  }>(CHANNELS.run);
  run(CHANNELS.run, { id });
  return response;
};

export const subscribeToRunProgress = (
  listener: (progress: AutomationRunProgress) => void,
): (() => void) => {
  if (!areAutomationsAvailable()) return () => undefined;
  const handler = (payload: AutomationRunProgress) => {
    if (payload?.runId) listener(payload);
  };
  on(CHANNELS.runProgress, handler);
  return () => removeListener(CHANNELS.runProgress, handler);
};

/** Projects what a run would do to the files waiting in the watch folder. */
export const previewAutomationRun = async (input: {
  trigger: AutomationTrigger;
  graph: AutomationGraph;
}): Promise<AutomationDryRunResult> => {
  if (!areAutomationsAvailable()) {
    return {
      ...emptyDryRunResult(),
      notice: 'Previews need the desktop app.',
    };
  }
  const response = oncePromise<AutomationDryRunResult>(CHANNELS.dryRun);
  run(CHANNELS.dryRun, input);
  return response;
};

/** @deprecated Chat-only save path. Prefer saveAutomationWorkflow. */
export const saveAutomation = async (input: {
  id?: number | null;
  name: string;
  instructions: string;
  model?: AgentModelSelection | null;
}): Promise<{ saved: boolean; error?: string }> => {
  if (!areAutomationsAvailable()) {
    return { saved: false, error: 'Automations need the desktop app.' };
  }
  const response = oncePromise<{ saved: boolean; error?: string }>(
    CHANNELS.save,
  );
  run(CHANNELS.save, input);
  return response;
};
